import React, { useContext, useEffect, useState } from 'react';
import { AlertTriangle, Map, CheckCircle, Clock, User, TrendingUp, Activity, FileText } from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import riskManagerStatsService from '../../../../services/riskManagerStatsService';
import RecentHistoriesSection from './RecentHistoriesSection';

export default function RisKManagerDashboardHome() {


    const { authTokens } = useContext(AuthContext);
    const [dashboardData, setDashboardData] = useState(null);
    const [recentHistories, setRecentHistories] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            if (!authTokens.access) {
            console.log('❌ No access token available');
            setLoading(false);
            return;
            }

            try {
                setLoading(true);
                console.log('🚀 Starting admin dashboard data fetch...');

                // Fetch all data in parallel
                const [statsResult, historiesResult] = await Promise.all([
                    riskManagerStatsService.getRiskManagerDashboardStats(authTokens.access),
                    riskManagerStatsService.getRiskManagerRecentHistories(authTokens.access)
                ]);

                if (statsResult.success) {
                    setDashboardData(statsResult.data);
                    console.log('✅ Dashboard stats loaded:', statsResult.data);
                }


                if (historiesResult.success) {
                    setRecentHistories(historiesResult.data);
                    console.log('✅ Recent histories loaded:', historiesResult.data);
                }

                console.log('✅ All API data loaded successfully!');
            } catch (error) {
                console.error('❌ Error fetching dashboard data:', error);
            } finally {
            setLoading(false);
            }
        };

        fetchDashboardData();
    }, [authTokens.access]);

    if (loading) {
    return (
        <div className="flex items-center justify-center h-64">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
        </div>
    );
    }

    const stats = dashboardData || {};
    const riskStatus = stats.risk_status || {};
    const processHealth = stats.process_health || {};
    const systemActivity = stats.system_activity || {};
    const teamOverview = stats.team_overview || {};
    return (
    <div className="space-y-6">
      {/* Risk Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Risques totaux</p>
              <p className="text-2xl font-semibold text-gray-900">{riskStatus.total_risks || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Risques approuvés</p>
              <p className="text-2xl font-semibold text-gray-900">{riskStatus.approved || 0}</p>
              <p className="text-xs text-green-600 mt-1">{riskStatus.approved_percentage?.toFixed(1) || 0}%</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Risques en attente</p>
              <p className="text-2xl font-semibold text-gray-900">{riskStatus.pending || 0}</p>
              <p className="text-xs text-orange-600 mt-1">{riskStatus.pending_percentage?.toFixed(1) || 0}%</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Clock className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Actions en retard</p>
              <p className="text-2xl font-semibold text-gray-900">{riskStatus.overdue_actions || 0}</p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Process Health & System Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <FileText className="h-5 w-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Santé des processus</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total processus</span>
              <span className="font-semibold text-gray-900">{processHealth.total_processes || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Processus publiés</span>
              <span className="font-semibold text-green-600">{processHealth.published_processes || 0} ({processHealth.published_percentage?.toFixed(0) || 0}%)</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Brouillons</span>
              <span className="font-semibold text-orange-600">{processHealth.draft_processes || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Contrôles à venir</span>
              <span className="font-semibold text-blue-600">{processHealth.upcoming_controls || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Contrôles en retard</span>
              <span className="font-semibold text-red-600">{processHealth.overdue_controls || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <div className="flex items-center mb-4">
            <Activity className="h-5 w-5 text-teal-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Activité du système</h3>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Risques créés</span>
              <span className="font-semibold text-gray-900">{systemActivity.risks_created || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Processus créés</span>
              <span className="font-semibold text-gray-900">{systemActivity.processes_created || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Activité  créés</span>
              <span className="font-semibold text-gray-900">{systemActivity.total_activities || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tâches en cours</span>
              <span className="font-semibold text-blue-600">{systemActivity.tasks_in_progress || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Tâches complétées</span>
              <span className="font-semibold text-green-600">{systemActivity.tasks_completed || 0}</span>
            </div>
          </div>
        </div>
      </div>

     {/* Recent Histories Section */}
     <RecentHistoriesSection data={recentHistories} loading={loading} />

    </div>
  );
}
